import { useCallback } from 'react'
import { useNavigate } from 'react-router'
import { useAuthStore } from 'stores/auth'

/**
 * Thrown by `fetchApi` on any non-2xx response (other than 401, which
 * redirects to /login). Carries the HTTP status + parsed body so callers
 * can branch on specific failure modes — e.g. the contact-new page
 * inspects `err.status === 409 && err.body?.error === 'duplicate'` to
 * render a merge-suggestion modal instead of a generic toast.
 */
export class FetchApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    statusText: string,
  ) {
    super(`${status} ${statusText}`)
    this.name = 'FetchApiError'
  }
}

/**
 * Pull a human-readable message out of whatever a mutation rejected with.
 *
 * Our server's `errorHandler` emits `{ "error": "..." }` on every failure,
 * so for `FetchApiError` we prefer that body string over the synthesized
 * `"500 Internal Server Error"` message. Falls through to a string-ish
 * representation for unknown error shapes so the caller can render
 * *something* rather than nothing.
 */
export function getApiErrorMessage(err: unknown, fallback = 'Something went wrong'): string {
  if (err instanceof FetchApiError) {
    const body = err.body
    if (body && typeof body === 'object' && 'error' in body) {
      const e = (body as { error: unknown }).error
      if (typeof e === 'string' && e.trim().length > 0) {
        return e
      }
    }
    return `${err.status} ${err.message.replace(/^\d+\s+/, '') || fallback}`
  }
  if (err instanceof Error && err.message) {
    return err.message
  }
  return fallback
}

export function useFetchApi() {
  const clearUser = useAuthStore((s) => s.clearUser)
  const navigate = useNavigate()

  const fetchApi = useCallback(
    async <T>(url: string, init?: RequestInit): Promise<T> => {
      const headers = new Headers(init?.headers)
      if (!headers.has('Content-Type') && !(init?.body instanceof FormData)) {
        headers.set('Content-Type', 'application/json')
      }
      const res = await fetch(url, { ...init, headers })
      if (res.status === 401) {
        clearUser()
        navigate('/login', { replace: true })
        throw new FetchApiError(res.status, null, res.statusText)
      }
      if (!res.ok) {
        let body: unknown = null
        try {
          body = await res.json()
        } catch {
          // body wasn't JSON; leave it null
        }
        throw new FetchApiError(res.status, body, res.statusText)
      }
      return res.json() as Promise<T>
    },
    [clearUser, navigate],
  )

  return { fetchApi }
}
